import { css, useTheme } from '@emotion/react';
import { Copy, FolderInput, Mic, MoreHorizontal, Play, Square, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { MusicStatusBadge } from './music-status-badge';

import type { ProjectListItem } from '@/lib/api-client';
import type { AppThemeColors } from '@/theme/emotion';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

type PipelineStatus = 'draft' | 'needs_analysis' | 'needs_rehearsal' | 'needs_music' | 'ready';

interface ScriptCardProps {
  script: ProjectListItem;
  onDelete: (id: string, title: string) => void;
  onMove?: (id: string, title: string, currentFolderId: string | null) => void;
  onDuplicate?: (id: string) => void;
  onPlayToggle?: (id: string) => void;
  isPlaying?: boolean;
}

export function formatDurationMinutes(ms: number | null): string {
  if (!ms || ms <= 0) return '';
  const minutes = Math.round(ms / 60000);
  if (minutes === 0) return '< 1 min';
  return `~${minutes} min`;
}

export function formatAbsoluteDate(date: string): string {
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

export function formatRelativeDate(date: string): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatAbsoluteDate(date);
}

export function countSectionsFromRawContent(rawContent: string): number {
  const trimmed = rawContent.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\n---\n/).filter((s) => s.trim()).length;
}

export function getContentPreview(rawContent: string, maxLength = 120): string {
  const cleaned = rawContent
    .replace(/\n---\n/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.slice(0, maxLength) + '...';
}

function getStatusBorder(status: PipelineStatus, colors: AppThemeColors): string {
  switch (status) {
    case 'ready':
      return colors.success;
    case 'needs_music':
      return colors.warning;
    case 'needs_analysis':
    case 'needs_rehearsal':
      return colors.info;
    default:
      return 'transparent';
  }
}

function getStatusBackground(status: PipelineStatus, colors: AppThemeColors): string {
  switch (status) {
    case 'ready':
      return colors.successMuted;
    case 'needs_music':
      return colors.warningMuted;
    case 'needs_analysis':
    case 'needs_rehearsal':
      return colors.infoMuted;
    default:
      return colors.surfaceElevated;
  }
}

export function ScriptCard({
  script,
  onDelete,
  onMove,
  onDuplicate,
  onPlayToggle,
  isPlaying,
}: ScriptCardProps) {
  const theme = useTheme();
  const duration = formatDurationMinutes(script.totalDurationMs);
  const relativeDate = formatRelativeDate(script.updatedAt);
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const borderColor = getStatusBorder(script.pipelineStatus, theme.colors);
  const bgColor = getStatusBackground(script.pipelineStatus, theme.colors);
  const isDraft = script.pipelineStatus === 'draft';

  // Position menu after render, flipping upward if it would overflow the viewport
  useLayoutEffect(() => {
    if (!menuOpen || !portalRef.current || !buttonRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = portalRef.current.getBoundingClientRect();
    const rightOffset = window.innerWidth - buttonRect.right;
    const spaceBelow = window.innerHeight - buttonRect.bottom - 4;

    if (menuRect.height > spaceBelow) {
      // Flip upward
      portalRef.current.style.bottom = `${window.innerHeight - buttonRect.top + 4}px`;
      portalRef.current.style.top = '';
    } else {
      portalRef.current.style.top = `${buttonRect.bottom + 4}px`;
      portalRef.current.style.bottom = '';
    }
    portalRef.current.style.right = `${rightOffset}px`;
  }, [menuOpen]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        portalRef.current &&
        !portalRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleMenuAction = useCallback((action: () => void) => {
    setMenuOpen(false);
    action();
  }, []);

  return (
    <li
      css={css`
        list-style: none;
        position: relative;
        background: ${bgColor};
        border: 1px solid ${theme.colors.border};
        border-left: 3px solid ${borderColor};
        border-radius: ${radii.md};
        padding: 20px ${spacing.lg};
        cursor: pointer;
        opacity: ${isDraft ? 0.7 : 1};
        transition:
          background 0.2s ease-in-out,
          border-color 0.2s ease-in-out;

        &:hover {
          background: ${isDraft ? bgColor : theme.colors.surfaceElevated};
          border-color: ${theme.colors.primary}30;
        }

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: ${spacing.md};
          margin-bottom: ${spacing.sm};
        `}
      >
        <h2
          css={css`
            margin: 0;
            font-size: ${fontSizes.xl};
            font-weight: 600;
          `}
        >
          <Link
            to={`/app/projects/${script.id}/edit`}
            css={css`
              color: ${theme.colors.text};
              text-decoration: none;
              &::after {
                content: '';
                position: absolute;
                inset: 0;
                z-index: 0;
              }
              &:hover {
                color: ${theme.colors.primary};
              }
              transition: color 0.2s ease-in-out;
            `}
          >
            {script.title}
          </Link>
        </h2>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.sm};
            flex-shrink: 0;
          `}
        >
          {script.previewAudioUrl && onPlayToggle && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlayToggle(script.id);
              }}
              aria-label={isPlaying ? 'Stop preview' : 'Play preview'}
              css={css`
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 1px solid ${theme.colors.border};
                background: ${isPlaying ? theme.colors.primary : 'transparent'};
                color: ${isPlaying ? theme.colors.primaryText : theme.colors.text};
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                position: relative;
                z-index: 1;
                transition: all 0.2s ease-in-out;
                &:hover {
                  border-color: ${theme.colors.primary};
                  background: ${isPlaying
                    ? theme.colors.primaryHover
                    : theme.colors.primary + '10'};
                }
              `}
            >
              {isPlaying ? (
                <Square size={14} aria-hidden="true" fill="currentColor" />
              ) : (
                <Play size={14} aria-hidden="true" fill="currentColor" />
              )}
            </button>
          )}

          {/* Contextual menu */}
          <div
            css={css`
              position: relative;
            `}
          >
            <button
              ref={buttonRef}
              type="button"
              aria-label="Script actions"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              css={css`
                position: relative;
                z-index: 1;
                width: 32px;
                height: 32px;
                border-radius: ${radii.sm};
                border: 1px solid transparent;
                background: transparent;
                color: ${theme.colors.textSecondary};
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                transition: all 0.2s ease-in-out;
                &:hover {
                  background: ${theme.colors.sidebarHoverBg};
                  color: ${theme.colors.text};
                  border-color: ${theme.colors.border};
                }
              `}
            >
              <MoreHorizontal size={18} aria-hidden="true" />
            </button>
            {menuOpen &&
              createPortal(
                <div
                  ref={portalRef}
                  role="menu"
                  css={css`
                    position: fixed;
                    background: ${theme.colors.surfaceElevated};
                    border: 1px solid ${theme.colors.border};
                    border-radius: ${radii.md};
                    box-shadow: ${shadows.elevated};
                    padding: ${spacing.xs} 0;
                    min-width: 140px;
                    z-index: 1000;
                  `}
                >
                  <Link
                    to={`/app/projects/${script.id}/edit`}
                    role="menuitem"
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.sm};
                      width: 100%;
                      text-align: left;
                      padding: ${spacing.sm} ${spacing.md};
                      background: none;
                      border: none;
                      cursor: pointer;
                      font-size: ${fontSizes.sm};
                      color: ${theme.colors.text};
                      text-decoration: none;
                      &:hover {
                        background: ${theme.colors.sidebarHoverBg};
                      }
                    `}
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/app/projects/${script.id}/rehearse`}
                    role="menuitem"
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.sm};
                      width: 100%;
                      text-align: left;
                      padding: ${spacing.sm} ${spacing.md};
                      background: none;
                      border: none;
                      cursor: pointer;
                      font-size: ${fontSizes.sm};
                      color: ${theme.colors.text};
                      text-decoration: none;
                      &:hover {
                        background: ${theme.colors.sidebarHoverBg};
                      }
                    `}
                  >
                    <Mic size={14} aria-hidden="true" />
                    Rehearse
                  </Link>
                  {onMove && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        handleMenuAction(() => onMove(script.id, script.title, script.folderId))
                      }
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: ${spacing.sm};
                        width: 100%;
                        text-align: left;
                        padding: ${spacing.sm} ${spacing.md};
                        background: none;
                        border: none;
                        cursor: pointer;
                        font-size: ${fontSizes.sm};
                        color: ${theme.colors.text};
                        &:hover {
                          background: ${theme.colors.sidebarHoverBg};
                        }
                      `}
                    >
                      <FolderInput size={14} aria-hidden="true" />
                      Move
                    </button>
                  )}
                  {onDuplicate && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleMenuAction(() => onDuplicate(script.id))}
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: ${spacing.sm};
                        width: 100%;
                        text-align: left;
                        padding: ${spacing.sm} ${spacing.md};
                        background: none;
                        border: none;
                        cursor: pointer;
                        font-size: ${fontSizes.sm};
                        color: ${theme.colors.text};
                        &:hover {
                          background: ${theme.colors.sidebarHoverBg};
                        }
                      `}
                    >
                      <Copy size={14} aria-hidden="true" />
                      Duplicate
                    </button>
                  )}
                  <div
                    css={css`
                      height: 1px;
                      background: ${theme.colors.border};
                      margin: ${spacing.xs} 0;
                    `}
                  />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleMenuAction(() => onDelete(script.id, script.title))}
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${spacing.sm};
                      width: 100%;
                      text-align: left;
                      padding: ${spacing.sm} ${spacing.md};
                      background: none;
                      border: none;
                      cursor: pointer;
                      font-size: ${fontSizes.sm};
                      color: ${theme.colors.error};
                      &:hover {
                        background: ${theme.colors.errorMuted};
                      }
                    `}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    Delete
                  </button>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>

      {/* Metadata line: status badge + duration + relative date */}
      <p
        css={css`
          margin: 0;
          font-size: ${fontSizes.sm};
          color: ${theme.colors.textMuted};
          display: flex;
          align-items: center;
          gap: 6px;
        `}
      >
        <MusicStatusBadge status={script.pipelineStatus} />
        {duration && (
          <>
            <span aria-hidden="true">&middot;</span>
            <span>{duration}</span>
          </>
        )}
        <span aria-hidden="true">&middot;</span>
        <span>{relativeDate}</span>
      </p>
    </li>
  );
}
